// ChatStartButton 제거됨 - 구매 제안을 통해서만 거래 진행
import { PageContainer } from "@/components/layout/PageContainer";
import { BuyerOfferCard } from "@/components/market/BuyerOfferCard";
import { MessageSendDialog } from "@/components/market/MessageSendDialog";
import { OfferFormDialog } from "@/components/market/OfferFormDialog";
import { OfferManagementCard } from "@/components/market/OfferManagementCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { tryOpenKakaoShareSheet } from "@/lib/kakao-share";
import { marketCategories, regions, type MarketListing } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  Eye,
  Heart,
  Mail,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Share2,
  ShieldCheck,
  Star,
  Truck,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";

export default function MarketDetail() {
  const [match, params] = useRoute<{ id: string }>("/market/:id");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  // Check if params is valid
  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              유효하지 않은 상품 ID입니다
            </p>
            <Button variant="outline" onClick={() => setLocation("/market")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const listingId = params.id;

  const { data: listing, isLoading } = useQuery<MarketListing>({
    queryKey: ["/api/market", listingId],
    queryFn: async () => {
      const res = await fetch(`/api/market/${listingId}`);
      if (!res.ok) throw new Error("Failed to fetch listing");
      const json = await res.json();
      // Handle both { ok: true, data: {...} } and direct {...} formats
      return json.data || json;
    },
    enabled: !!listingId,
  });

  // Fetch product details for new market system (pickup places, times, payment methods, negotiable)
  const { data: productDetails } = useQuery<{
    negotiable?: boolean;
    pickup_places?: Array<{ id: number; name: string; address?: string }>;
    pickup_times?: Array<{
      id: number;
      day: string;
      start: string;
      end: string;
    }>;
    payment_methods?: string[];
  } | null>({
    queryKey: ["/api/market/products", listingId],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/market/products/${listingId}`);
        if (!res.ok) return null;
        const json = await res.json();
        const product = json.data || json;
        return {
          negotiable: product.negotiable ?? false,
          pickup_places: product.pickup_places || [],
          pickup_times: product.pickup_times || [],
          payment_methods: product.payment_methods || [],
        };
      } catch {
        return null;
      }
    },
    enabled: !!listingId,
  });

  // Check if current user is the owner (needed for conditional queries)
  const isOwner = user?.uid === listing?.userId;

  // Check if there's a confirmed offer (for message sending)
  const { data: confirmedOffer } = useQuery<{ id: number } | null>({
    queryKey: ["/api/market/offers", "confirmed", listingId, user?.uid],
    queryFn: async () => {
      if (!user || !listingId) return null;
      try {
        const res = await fetch(
          `/api/market/offers?role=buyer&status=confirmed&product_id=${listingId}`
        );
        if (!res.ok) return null;
        const json = await res.json();
        const offers = json.items || json.data || [];
        // Find confirmed offer for this product
        const confirmed = offers.find(
          (offer: any) =>
            offer.product_id === Number(listingId) &&
            offer.status === "confirmed"
        );
        return confirmed || null;
      } catch {
        return null;
      }
    },
    enabled: !!user && !!listingId && !isOwner,
  });

  const hasConfirmedOffer = !!confirmedOffer;

  const { data: favoriteData } = useQuery({
    queryKey: ["/api/market-favorites/check", user?.uid, listingId],
    queryFn: async () => {
      const res = await fetch(
        `/api/market-favorites/check/${user?.uid}/${listingId}`
      );
      if (!res.ok) throw new Error("Failed to check favorite");
      return res.json();
    },
    enabled: !!user && !!listingId,
  });

  // Fetch seller rating stats
  const { data: sellerStats } = useQuery<{
    averageRating: number;
    totalReviews: number;
  }>({
    queryKey: ["/api/market-reviews/stats", listing?.userId],
    queryFn: async () => {
      const res = await fetch(`/api/market-reviews/stats/${listing?.userId}`);
      if (!res.ok) throw new Error("Failed to fetch stats");
      return res.json();
    },
    enabled: !!listing?.userId,
  });

  // Track view count - only once per page load
  const [viewTracked, setViewTracked] = useState(false);

  const incrementViewMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/market/${listingId}/view`, {}),
    onSuccess: () => {
      setViewTracked(true);
    },
    onError: (error) => {
      // Silently fail - don't show error to user for view tracking
      console.error("[MarketDetail] View tracking error:", error);
    },
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: async () => {
      if (
        (favoriteData as { isFavorited?: boolean; favorite?: { id: string } })
          ?.isFavorited
      ) {
        return apiRequest(
          "DELETE",
          `/api/market-favorites/${
            (favoriteData as { favorite: { id: string } }).favorite.id
          }`,
          {}
        );
      } else {
        return apiRequest("POST", "/api/market-favorites", {
          userId: user?.uid,
          listingId: listingId,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market-favorites/check", user?.uid, listingId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/market", listingId] });
      toast({
        title: (favoriteData as { isFavorited?: boolean })?.isFavorited
          ? "찜하기 취소"
          : "찜하기 완료",
        description: (favoriteData as { isFavorited?: boolean })?.isFavorited
          ? "찜 목록에서 제거했습니다"
          : "찜 목록에 추가했습니다",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) =>
      apiRequest("PATCH", `/api/market/${listingId}/status`, { status }),
    onSuccess: (_data, status) => {
      queryClient.invalidateQueries({ queryKey: ["/api/market", listingId] });

      // Auto-prompt review when status changes to "sold"
      if (status === "sold") {
        toast({
          title: "🎉 거래가 완료되었습니다!",
          description: "거래 후기를 남겨주시겠어요?",
          action: (
            <Button
              size="sm"
              onClick={() => setReviewDialogOpen(true)}
              data-testid="toast-review-prompt"
            >
              후기 작성
            </Button>
          ),
        });
      } else {
        toast({
          title: "상태가 변경되었습니다",
        });
      }
    },
  });

  const submitReviewMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/market-reviews", {
        listingId: listing?.id,
        reviewerId: user?.uid,
        reviewerName: user?.displayName || user?.email || "익명",
        reviewerPhoto: user?.photoURL || null,
        revieweeId: listing?.userId,
        revieweeName: listing?.userDisplayName,
        revieweePhoto: listing?.userPhotoUrl || null,
        rating: reviewRating,
        comment: reviewComment || null,
        reviewType: isOwner ? "seller_to_buyer" : "buyer_to_seller",
        transactionCompleted: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market-reviews/stats", listing?.userId],
      });
      setReviewDialogOpen(false);
      setReviewComment("");
      setReviewRating(5);
      toast({
        title: "리뷰가 등록되었습니다",
        description: "소중한 의견 감사합니다!",
      });
    },
  });

  // Increment view on mount - only once
  useEffect(() => {
    if (
      listingId &&
      !isLoading &&
      listing &&
      !viewTracked &&
      !incrementViewMutation.isPending &&
      !incrementViewMutation.isSuccess
    ) {
      incrementViewMutation.mutate();
    }
  }, [listingId]); // Only run when listingId changes

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-96 bg-muted rounded-lg" />
            <div className="h-32 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-2xl font-bold mb-2">게시글을 찾을 수 없습니다</h2>
        <Button onClick={() => setLocation("/market")} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          목록으로 돌아가기
        </Button>
      </div>
    );
  }

  const formatPrice = (cents: number | null | undefined) => {
    if (cents === null || cents === undefined) return "가격 문의";
    if (cents === 0) return "무료나눔";
    if (cents === -1) return "가격협의";
    return `$${(cents / 100).toFixed(0)} AUD`;
  };

  const category = marketCategories.find((c) => c.id === listing.category);
  const stateRegion = regions.find((r) => r.id === listing.state);

  return (
    <PageContainer size="default">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => setLocation("/market")}
        className="mb-4"
        data-testid="button-back"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Images & Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Images */}
          <Card>
            <CardContent className="p-0">
              {listing.images &&
              Array.isArray(listing.images) &&
              listing.images.length > 0 ? (
                <>
                  <div className="relative h-96 bg-muted overflow-hidden rounded-t-lg">
                    <img
                      src={listing.images[selectedImageIndex]}
                      alt={listing.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {listing.isPremium && (
                      <Badge className="absolute top-4 left-4 bg-primary">
                        프리미엄
                      </Badge>
                    )}
                  </div>
                  {listing.images.length > 1 && (
                    <div className="p-4 flex gap-2 overflow-x-auto">
                      {listing.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 hover-elevate ${
                            idx === selectedImageIndex
                              ? "border-primary"
                              : "border-transparent"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`${listing.title} ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="105" dx="100" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="h-96 flex items-center justify-center bg-muted rounded-t-lg">
                  <Package className="w-24 h-24 text-muted-foreground/30" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">상세 설명</h2>
            </CardHeader>
            <CardContent>
              {listing.description ? (
                <p
                  className="whitespace-pre-wrap text-muted-foreground"
                  data-testid="text-description"
                >
                  {listing.description}
                </p>
              ) : (
                <p className="text-muted-foreground italic">
                  상세 설명이 없습니다.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pickup Locations */}
          {listing.pickupLocations && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  픽업 가능 장소
                </h2>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {listing.pickupLocations}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Phase 6.1: Safety Banner - Always visible */}
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  안전 거래 안내
                </h2>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>공공장소에서 만나 거래하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>직거래 시 현금 거래를 권장합니다</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>의심스러운 계좌 이체 요청은 거절하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>거래 전 상품을 직접 확인하세요</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Right - Info & Actions */}
        <div className="space-y-4">
          {/* Main Info Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h1 className="text-2xl font-bold" data-testid="text-title">
                  {listing.title}
                </h1>
                {user && !isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleFavoriteMutation.mutate()}
                    data-testid="button-favorite"
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        (favoriteData as { isFavorited?: boolean })?.isFavorited
                          ? "fill-red-500 text-red-500"
                          : ""
                      }`}
                    />
                  </Button>
                )}
              </div>

              <div
                className="text-3xl font-bold text-primary"
                data-testid="text-price"
              >
                {formatPrice(listing.priceCents)}
              </div>

              <div className="flex gap-2 flex-wrap mt-3">
                {category && (
                  <Badge variant="secondary">{category.label}</Badge>
                )}
                {listing.deliveryAvailable && (
                  <Badge
                    variant="secondary"
                    className="bg-green-600/10 text-green-700 dark:text-green-400"
                  >
                    <Truck className="w-3 h-3 mr-1" />
                    배송가능
                  </Badge>
                )}
              </div>
            </CardHeader>

            <Separator />

            <CardContent className="pt-6 space-y-4">
              {/* Location */}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">위치</p>
                  <p className="text-sm text-muted-foreground">
                    {listing.suburb && `${listing.suburb}, `}
                    {stateRegion?.label}
                    {listing.postcode && ` ${listing.postcode}`}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {listing.views || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  {listing.favorites || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {listing.createdAt &&
                  !isNaN(new Date(listing.createdAt).getTime())
                    ? formatDistanceToNow(new Date(listing.createdAt), {
                        addSuffix: true,
                        locale: ko,
                      })
                    : "방금 전"}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Seller Info */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold">판매자 정보</h2>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={listing.userPhotoUrl || undefined} />
                  <AvatarFallback>
                    <User className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{listing.userDisplayName}</p>
                  <p className="text-sm text-muted-foreground">
                    {listing.userEmail}
                  </p>
                  {sellerStats && sellerStats.totalReviews > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">
                        {sellerStats.averageRating.toFixed(1)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({sellerStats.totalReviews}개 리뷰)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!isOwner && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    {/* Trust Indicators (Mock data for now) */}
                    <div className="grid grid-cols-3 gap-3 text-center p-3 bg-muted/30 rounded-lg">
                      <div>
                        <div className="text-lg font-semibold text-primary">
                          95%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          응답률
                        </div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-primary">
                          2분
                        </div>
                        <div className="text-xs text-muted-foreground">
                          평균 응답
                        </div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-primary">
                          12회
                        </div>
                        <div className="text-xs text-muted-foreground">
                          거래
                        </div>
                      </div>
                    </div>

                    {/* Chat Button 제거됨 - 구매 제안을 통해서만 거래 진행 */}

                    {/* New Market System: Offer Form (Buyer) */}
                    {!isOwner && user && (
                      <OfferFormDialog
                        productId={Number(listing.id)}
                        productPriceCents={listing.priceCents || undefined}
                        negotiable={productDetails?.negotiable ?? false}
                        pickupPlaces={productDetails?.pickup_places || []}
                        pickupTimes={productDetails?.pickup_times || []}
                        paymentMethods={productDetails?.payment_methods || []}
                        onSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: ["/api/market/offers"],
                          });
                        }}
                      />
                    )}

                    {/* New Market System: Message Send (Buyer) - 구매 확정 후에만 가능 */}
                    {!isOwner &&
                      user &&
                      listing.userId &&
                      hasConfirmedOffer && (
                        <MessageSendDialog
                          receiverId={listing.userId}
                          senderId={user.uid}
                          productId={Number(listing.id)}
                          trigger={
                            <Button
                              variant="outline"
                              className="w-full"
                              size="sm"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              메시지 보내기
                            </Button>
                          }
                        />
                      )}

                    {/* Secondary: Kakao Chat Button (if available) - 숨김 처리 (나중에 필요시 사용) */}
                    {/* {listing.kakaoUrl && !isOwner && (
                      <a
                        href={`/api/redirect/kakao?type=market&id=${listing.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button
                          variant="outline"
                          className="w-full border-[#FEE500] text-[#000000] hover:bg-[#FEE500]/10"
                          size="lg"
                          data-testid="button-kakao"
                        >
                          <MessageCircle className="w-5 h-5 mr-2" />
                          카카오톡 오픈채팅
                        </Button>
                      </a>
                    )} */}

                    {/* Secondary: Other contact methods */}
                    {((listing.contactMethod === "phone" ||
                      listing.contactMethod === "both") &&
                      listing.phoneNumber) ||
                    listing.contactMethod === "email" ||
                    listing.contactMethod === "both" ? (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-center py-2">
                          기타 연락 수단 보기
                        </summary>
                        <div className="space-y-2 mt-2">
                          {(listing.contactMethod === "phone" ||
                            listing.contactMethod === "both") &&
                            listing.phoneNumber && (
                              <a href={`tel:${listing.phoneNumber}`}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-start"
                                  data-testid="button-phone"
                                >
                                  <Phone className="w-4 h-4 mr-2" />
                                  {listing.phoneNumber}
                                </Button>
                              </a>
                            )}
                          {(listing.contactMethod === "email" ||
                            listing.contactMethod === "both") && (
                            <a href={`mailto:${listing.userEmail}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-start"
                                data-testid="button-email"
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                이메일 보내기
                              </Button>
                            </a>
                          )}
                        </div>
                      </details>
                    ) : null}
                  </div>
                </>
              )}

              {isOwner && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    {/* Transaction Status Control */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium">거래 상태</p>
                      <Select
                        value={listing.status}
                        onValueChange={(value) =>
                          updateStatusMutation.mutate(value)
                        }
                        disabled={updateStatusMutation.isPending}
                      >
                        <SelectTrigger data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">판매중</SelectItem>
                          <SelectItem value="reserved">예약중</SelectItem>
                          <SelectItem value="sold">거래완료</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setLocation(`/market/edit/${listing.id}`)}
                      data-testid="button-edit"
                    >
                      수정하기
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setLocation("/market/my")}
                    >
                      내 판매글 보기
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* New Market System: Buyer Offer Card */}
          {!isOwner && user && (
            <BuyerOfferCard productId={Number(listing.id)} buyerId={user.uid} />
          )}

          {/* New Market System: Offer Management (Seller) */}
          {isOwner && user && (
            <OfferManagementCard
              productId={Number(listing.id)}
              sellerId={user.uid}
            />
          )}

          {/* Review Section - Only show if transaction is completed */}
          {listing.status === "sold" && user && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold">거래 후기</h2>
              </CardHeader>
              <CardContent>
                <Dialog
                  open={reviewDialogOpen}
                  onOpenChange={setReviewDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      className="w-full"
                      data-testid="button-write-review"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      {isOwner ? "구매자" : "판매자"}에게 후기 남기기
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>거래 후기 작성</DialogTitle>
                      <DialogDescription>
                        {isOwner ? "구매자" : "판매자"}와의 거래는 어떠셨나요?
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      {/* Rating Stars */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">평점</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewRating(star)}
                              className="hover-elevate rounded"
                              data-testid={`button-star-${star}`}
                            >
                              <Star
                                className={`w-8 h-8 ${
                                  star <= reviewRating
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-muted-foreground"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Comment */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          후기 (선택사항)
                        </label>
                        <Textarea
                          placeholder="거래 경험을 공유해주세요..."
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          rows={4}
                          data-testid="input-review-comment"
                        />
                      </div>

                      {/* Submit Button */}
                      <Button
                        className="w-full"
                        onClick={() => submitReviewMutation.mutate()}
                        disabled={submitReviewMutation.isPending}
                        data-testid="button-submit-review"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        후기 등록
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}

          {/* Share Button - ③B "카카오톡으로 공유" (16장): Kakao Talk share
              sheet first, clipboard fallback when Kakao isn't available. */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              const opened = tryOpenKakaoShareSheet({
                path: `/market/${listing.id}`,
                contentType: "market",
                title: listing.title,
                description: listing.description || undefined,
                imageUrl: listing.images?.[0] || undefined,
                ctaLabel: "물건 보러가기",
              });
              if (opened) return;
              navigator.clipboard.writeText(window.location.href);
              toast({ title: "링크가 복사되었습니다" });
            }}
          >
            <Share2 className="w-4 h-4 mr-2" />
            카카오톡으로 공유
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
